import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const AXIS_TICK = { fill: "#6B7280", fontSize: 11 };
const DONUT_COLORS = ["#F5CB2B", "#3B82F6", "#16A34A", "#A855F7", "#F97316"];

function ChartCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-gray-700">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[280px] w-full overflow-hidden">
          <ResponsiveContainer width="100%" height="100%">
            {children as React.ReactElement}
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

export interface StatusByDayDatum {
  dia: string;
  validas: number;
  invalidadas: number;
  falhas: number;
}

/** Barras empilhadas por dia (EFE/STA/ERR). */
export function StatusByDayChart({ data }: { data: StatusByDayDatum[] }) {
  return (
    <ChartCard title="Etiquetas por dia (por status)">
      <BarChart data={data} margin={{ top: 8, right: 16, left: -8, bottom: 8 }}>
        <CartesianGrid strokeDasharray="2 2" vertical={false} stroke="#E5E7EB" />
        <XAxis dataKey="dia" tick={AXIS_TICK} />
        <YAxis tick={AXIS_TICK} allowDecimals={false} />
        <Tooltip />
        <Legend />
        <Bar dataKey="validas" name="Válidas" stackId="s" fill="#16A34A" />
        <Bar dataKey="invalidadas" name="Invalidadas" stackId="s" fill="#D97706" />
        <Bar dataKey="falhas" name="Falhas" stackId="s" fill="#DC2626" />
      </BarChart>
    </ChartCard>
  );
}

export interface NamedValue {
  name: string;
  value: number;
}

/** Donut de composição (ex.: justificativas por contexto). */
export function CompositionDonut({
  title,
  data,
}: {
  title: string;
  data: NamedValue[];
}) {
  return (
    <ChartCard title={title}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          innerRadius={60}
          outerRadius={100}
          paddingAngle={2}
        >
          {data.map((entry, index) => (
            <Cell
              key={entry.name}
              fill={DONUT_COLORS[index % DONUT_COLORS.length]}
            />
          ))}
        </Pie>
        <Tooltip />
        <Legend />
      </PieChart>
    </ChartCard>
  );
}

/** Ranking horizontal (top-N) — ex.: lojas por divergência / motivos. */
export function RankingBarChart({
  title,
  data,
  color = "#F5CB2B",
}: {
  title: string;
  data: NamedValue[];
  color?: string;
}) {
  return (
    <ChartCard title={title}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
      >
        <CartesianGrid strokeDasharray="2 2" horizontal={false} stroke="#E5E7EB" />
        <XAxis type="number" tick={AXIS_TICK} allowDecimals={false} />
        <YAxis
          type="category"
          dataKey="name"
          tick={AXIS_TICK}
          width={140}
          interval={0}
        />
        <Tooltip />
        <Bar dataKey="value" name="Total" fill={color} radius={[0, 4, 4, 0]} />
      </BarChart>
    </ChartCard>
  );
}
